import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, User, Check, X, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";
import { toast } from "sonner";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const TEACHERS = [
  { id: "t1", name: "[Teacher Name]", subject: "Mathematics" },
  { id: "t2", name: "[Teacher Name]", subject: "English Language" },
  { id: "t3", name: "[Teacher Name]", subject: "Integrated Science" },
  { id: "t4", name: "[Teacher Name]", subject: "Social Studies" },
  { id: "t5", name: "[Teacher Name]", subject: "RME" },
  { id: "t6", name: "[Teacher Name]", subject: "ICT" },
];

const REASONS = [
  "Academic Progress Discussion",
  "Behavioral Concern",
  "Exam Preparation Strategy",
  "Career Guidance",
  "Special Needs Support",
  "General Check-in",
  "Other",
];

const STATUS_COLORS = {
  confirmed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  completed: "bg-blue-100 text-blue-700",
};

const AppointmentBookingPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [view, setView] = useState("book");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const today = new Date();
  const minDate = today.toISOString().split("T")[0];
  const maxDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  useEffect(() => {
    if (user && token) fetchAppointments();
  }, [user, token]);

  const fetchAppointments = async () => {
    try {
      const data = await apiCall("GET", "/appointments", null, token);
      setAppointments(data);
    } catch (err) { console.error("Failed to fetch appointments:", err); }
  };

  const fetchSlots = async (teacherId, date) => {
    setLoadingSlots(true);
    try {
      const data = await apiCall("GET", `/appointments/slots?teacher_id=${teacherId}&date=${date}`);
      setAvailableSlots(data.available_slots || []);
    } catch (err) { console.error("Failed to fetch slots:", err); }
    setLoadingSlots(false);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSlot("");
    if (selectedTeacher && date) fetchSlots(selectedTeacher.id, date);
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setSelectedSlot("");
    setAvailableSlots([]);
    if (selectedDate) fetchSlots(teacher.id, selectedDate);
  };

  const handleBook = async () => {
    if (!user) { toast.error("Please sign in"); return; }
    if (!selectedTeacher || !selectedDate || !selectedSlot || !reason) {
      toast.error("Please fill all required fields"); return;
    }
    setSubmitting(true);
    try {
      await apiCall("POST", "/appointments", {
        teacher_id: selectedTeacher.id,
        teacher_name: selectedTeacher.name,
        date: selectedDate,
        time_slot: selectedSlot,
        reason,
        notes,
      }, token);
      toast.success("Appointment booked successfully!");
      setSelectedSlot("");
      setReason("");
      setNotes("");
      fetchAppointments();
      fetchSlots(selectedTeacher.id, selectedDate);
      setView("my");
    } catch (err) {
      toast.error(err?.detail || "Failed to book appointment");
    }
    setSubmitting(false);
  };

  const handleCancel = async (id) => {
    try {
      await apiCall("PUT", `/appointments/${id}`, { status: "cancelled" }, token);
      toast.success("Appointment cancelled");
      fetchAppointments();
    } catch (err) { toast.error("Failed to cancel"); }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-[#002147] mb-4">Please sign in to book appointments</p>
          <button onClick={() => navigate("/portal")} className={`px-6 py-3 bg-[#002147] text-white rounded-xl ${btn3D}`}>Sign In</button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="appointment-booking-page">
      <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-8">
        <div className="max-w-5xl mx-auto px-4">
          <button onClick={() => navigate(-1)} className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`} data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <Calendar size={28} />
            <div>
              <h1 className="text-2xl font-bold">Parent-Teacher Appointments</h1>
              <p className="text-white/70 text-sm">Book a meeting with your child's teacher</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Tab Toggle */}
        <div className="flex gap-2">
          <button onClick={() => setView("book")} className={`px-5 py-2.5 rounded-full text-sm font-medium ${view === "book" ? "bg-[#002147] text-white" : "bg-white/80 text-[#002147]"} ${btn3D}`} data-testid="tab-book">
            <Plus size={14} className="inline mr-1" /> Book New
          </button>
          <button onClick={() => setView("my")} className={`px-5 py-2.5 rounded-full text-sm font-medium ${view === "my" ? "bg-[#002147] text-white" : "bg-white/80 text-[#002147]"} ${btn3D}`} data-testid="tab-my">
            <Calendar size={14} className="inline mr-1" /> My Appointments ({appointments.length})
          </button>
        </div>

        {view === "book" ? (
          <div className="space-y-5">
            {/* Teacher Selection */}
            <Card data-testid="teacher-select-card">
              <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2"><User size={16} /> Select Teacher</CardTitle></CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {TEACHERS.map(t => (
                    <button key={t.id} onClick={() => handleTeacherSelect(t)}
                      className={`p-3 rounded-xl text-left transition-all ${selectedTeacher?.id === t.id ? "bg-[#002147] text-white" : "bg-gray-50 hover:bg-gray-100 text-[#002147]"} ${btn3D}`}
                      data-testid={`teacher-${t.id}`}>
                      <p className="font-semibold text-sm">{t.name}</p>
                      <p className={`text-xs ${selectedTeacher?.id === t.id ? "text-white/70" : "text-gray-500"}`}>{t.subject}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Date & Time */}
            {selectedTeacher && (
              <Card data-testid="date-time-card">
                <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2"><Calendar size={16} /> Select Date & Time</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Date</label>
                    <input type="date" value={selectedDate} onChange={e => handleDateChange(e.target.value)}
                      min={minDate} max={maxDate}
                      className="w-full px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-400"
                      data-testid="date-input" />
                  </div>
                  {selectedDate && (
                    <div>
                      <label className="text-sm text-gray-600 block mb-2">Available Slots</label>
                      {loadingSlots ? <p className="text-sm text-gray-400">Loading slots...</p> : (
                        <div className="flex flex-wrap gap-2">
                          {availableSlots.map(slot => (
                            <button key={slot} onClick={() => setSelectedSlot(slot)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium ${selectedSlot === slot ? "bg-green-600 text-white" : "bg-gray-100 text-[#002147] hover:bg-gray-200"} ${btn3D}`}
                              data-testid={`slot-${slot.replace(/\s+/g, '-')}`}>
                              <Clock size={12} className="inline mr-1" /> {slot}
                            </button>
                          ))}
                          {availableSlots.length === 0 && <p className="text-sm text-gray-400">No slots available for this date</p>}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Reason & Notes */}
            {selectedSlot && (
              <Card data-testid="reason-card">
                <CardHeader className="py-3"><CardTitle className="text-base">Meeting Details</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Reason *</label>
                    <select value={reason} onChange={e => setReason(e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-400"
                      data-testid="reason-select">
                      <option value="">Select a reason...</option>
                      {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Additional Notes (optional)</label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                      placeholder="Anything the teacher should know..."
                      className="w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-400 resize-none"
                      data-testid="notes-input" />
                  </div>
                  <button onClick={handleBook} disabled={submitting}
                    className={`w-full py-3 bg-green-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 ${btn3D} disabled:opacity-50`}
                    data-testid="book-btn">
                    <Check size={18} /> {submitting ? "Booking..." : "Confirm Booking"}
                  </button>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {appointments.map(a => (
              <Card key={a.id} className="hover:shadow-lg transition-shadow" data-testid={`appointment-${a.id}`}>
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#002147] rounded-2xl flex items-center justify-center text-white flex-shrink-0 shadow-md">
                    <Calendar size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-[#002147]">{a.teacher_name}</p>
                      <Badge className={STATUS_COLORS[a.status] || "bg-gray-100"}>{a.status}</Badge>
                    </div>
                    <p className="text-sm text-gray-600">{a.reason}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      <Calendar size={10} className="inline mr-1" />{a.date} &bull; <Clock size={10} className="inline mr-1" />{a.time_slot}
                    </p>
                  </div>
                  {a.status === "confirmed" && (
                    <button onClick={() => handleCancel(a.id)}
                      className={`px-4 py-2 bg-red-100 text-red-600 text-sm font-medium rounded-lg ${btn3D}`}
                      data-testid={`cancel-${a.id}`}>
                      <X size={14} className="inline mr-1" /> Cancel
                    </button>
                  )}
                </CardContent>
              </Card>
            ))}
            {appointments.length === 0 && (
              <div className="text-center py-12">
                <Calendar size={48} className="mx-auto text-[#002147]/20 mb-3" />
                <p className="text-[#002147]/50 font-medium">No appointments booked yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentBookingPage;
